import { useTheme } from "@mui/material";
import { useAuthorizationServiceV1 } from "@rw1/api-services";
import { Authorization_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import { useParams } from "react-router-dom";
import { ConfirmationDialog } from "../../../components";
import { QueryKeys } from "../../../hooks/QueryKeys";
import { useLocalization } from "../../../hooks/useLocalization";
import { TUserRoleRow } from "./RolesTab";

interface RevokeRoleDialogProps {
    open: boolean;
    userId: string;
    ctx: TUserRoleRow | null;
    onClose: () => void;
}

export const RevokeRoleDialog: React.FC<RevokeRoleDialogProps> = ({
    open,
    userId,
    ctx,
    onClose,
}) => {
    const { companyId } = useParams();
    const { t } = useLocalization();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const authorizationServiceV1 = useAuthorizationServiceV1();

    const ctxRef = useRef(ctx);

    useEffect(() => {
        if (open && ctx) {
            ctxRef.current = ctx;
        }
    }, [open, ctx]);

    const displayCtx = open ? ctx : ctxRef.current;

    const mutation = useMutation({
        mutationFn: async () => {
            if (!displayCtx) {
                throw new Error("No active role context");
            }

            return await authorizationServiceV1.revokeUserRole(
                userId,
                displayCtx.roleId,
                displayCtx.organizationId ?? undefined
            );
        },
        onMutate: async () => {
            const queryKey = [QueryKeys.UserRoles, userId];
            await queryClient.cancelQueries({ queryKey });

            const previousRoles =
                queryClient.getQueryData<Authorization_V1.UserRole[]>(queryKey);

            if (previousRoles && displayCtx) {
                queryClient.setQueryData<Authorization_V1.UserRole[]>(
                    queryKey,
                    previousRoles.filter(
                        (r) =>
                            !(
                                r.roleId === displayCtx.roleId &&
                                r.organizationId === displayCtx.organizationId
                            )
                    )
                );
            }

            return { previousRoles };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousRoles) {
                queryClient.setQueryData(
                    [QueryKeys.UserRoles, userId],
                    context.previousRoles
                );
            }
        },
        onSettled: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.UserRoles, userId],
            });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.OrganizationUserRoles, companyId] });
        },
        onSuccess: () => {
            onClose();
        },
    });

    if (!displayCtx) return null;

    const handleConfirm = async () => {
        try {
            await mutation.mutateAsync();
        } catch {
            // Errors managed by Query / state
        }
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <ConfirmationDialog.Root maxWidth="xs" onClose={handleCancel} open={open}>
            <ConfirmationDialog.Title iconColor={theme.colors.orange} showIcon>
                {t("users.roles.revokeRole")}
            </ConfirmationDialog.Title>

            <ConfirmationDialog.Content error={mutation.error}>
                <ConfirmationDialog.Text>
                    <Trans
                        i18nKey="users.roles.revokeRoleConfirmation"
                        values={{
                            roleName: displayCtx.roleName,
                            companyName: displayCtx.companyName,
                        }}
                    />
                </ConfirmationDialog.Text>
            </ConfirmationDialog.Content>

            <ConfirmationDialog.Actions>
                <ConfirmationDialog.Button
                    disabled={mutation.isPending}
                    onClick={handleCancel}
                    variant="contained"
                >
                    {t("users.roles.cancel")}
                </ConfirmationDialog.Button>

                <ConfirmationDialog.Button
                    isLoading={mutation.isPending}
                    loadingText={t("users.roles.revoking")}
                    onClick={() => void handleConfirm()}
                    variant="secondary"
                >
                    {t("users.roles.revokeRole")}
                </ConfirmationDialog.Button>
            </ConfirmationDialog.Actions>
        </ConfirmationDialog.Root>
    );
};
